import {IServiceFactory} from "../service-factory.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {NullableUndefinedString} from "../../types/nullable-types";
import {Check} from "../../types/type-checking";
import {DeeplinkSearchQueryParamsEnum} from "../deep-links/deeplink-search-query-params.enum";
import {PsoUserAgreementEnum, PsoUserOptionsEnum} from "./flight-search-controller/pso.enums";
import {FlightSearchControllerModel} from "./flight-search-controller/flight-search-controller.model";
import {UserSessionStorageKeys} from "../storage/user-session-storage/user-session-storage-keys";
import {IFlightSearchFields} from "./flight-search-controller/flight-search-controller-view-model.interface";
import {IRoundTripLowFareReader} from "../low-fare/low-fare-readers/low-fare-reader.interface";

export class FlightSearchDeepLinkHandler {
    constructor(private readonly services: IServiceFactory, private readonly getLowFareReader: () => IRoundTripLowFareReader) {
        makeObservable<this, '_hasDeepLinkErrors'>(this, {
            _hasDeepLinkErrors: observable.ref
        });
    }

    private _hasDeepLinkErrors: boolean = false;
    get hasDeepLinkErrors(): boolean {
        return this._hasDeepLinkErrors;
    }

    set hasDeepLinkErrors(value: boolean) {
        runInAction(() => {
            this._hasDeepLinkErrors = value;
        })
    }

    async createSearchController(): Promise<FlightSearchControllerModel | null> {


        if(!this.services.navigator.currentRoute.equals(this.services.navigator.routes.deepLink.search)) {
            return null;
        }




        const searchControllerFields: Partial<IFlightSearchFields> = {};


        const queryParamsValues = this._getDeepLinkSearchParams();

        //const agent = queryParams[DeeplinkSearchQueryParamsEnum.Agent];
        const culture = queryParamsValues[DeeplinkSearchQueryParamsEnum.Culture];
        const currency = queryParamsValues[DeeplinkSearchQueryParamsEnum.Currency];

        this._setLanguageAndCurrency(culture, currency);


        searchControllerFields.isOneWayDepartureTrip = queryParamsValues[DeeplinkSearchQueryParamsEnum.IsRoundTrip] !== 'true';
        searchControllerFields.isOneWayReturnTrip = false;

        searchControllerFields.departureOrigin = this.services.stations.tryGetStation(queryParamsValues[DeeplinkSearchQueryParamsEnum.Origin1]) ?? undefined;
        searchControllerFields.departureDestination = this.services.stations.tryGetStation(queryParamsValues[DeeplinkSearchQueryParamsEnum.Destination1]) ?? undefined;
        searchControllerFields.departureDate = this.services.time.tryConvertToDate(queryParamsValues[DeeplinkSearchQueryParamsEnum.DepartureDate1]) ?? undefined;
        searchControllerFields.returnOrigin = undefined;
        searchControllerFields.returnDestination = undefined;
        if(searchControllerFields.isOneWayDepartureTrip) {
            searchControllerFields.returnDate = null;
        } else {
            searchControllerFields.returnDate = this.services.time.tryConvertToDate(queryParamsValues[DeeplinkSearchQueryParamsEnum.ReturnDate1]);
        }
        searchControllerFields.psoUserOption = this._getPsoUserOptionQueryParam(queryParamsValues);
        searchControllerFields.psoUserAgreement = this._getPsoUserAgreementQueryParam(queryParamsValues);
        searchControllerFields.agent = queryParamsValues[DeeplinkSearchQueryParamsEnum.Agent];


        return new FlightSearchControllerModel(this.services, UserSessionStorageKeys.FlightSearchSearchControllerStorage, {
            getLowFareReader: this.getLowFareReader,
            initialFieldsValues: searchControllerFields,
            initialPassengersCount: this._extractPassengerTypesCountFromQueryParams()
        });
    }

    private _extractPassengerTypesCountFromQueryParams(): Record<string, number> {
        const allPassengerTypeCodes = this.services.passengerTypes.getAllTypeCodes();
        const passengersCount: Record<string, number> = {};
        const queryParamsValues = this.services.navigator.getQueryParamsValues(...allPassengerTypeCodes);

        for(let passengerTypeCode of allPassengerTypeCodes) {
            const count = parseInt(queryParamsValues[passengerTypeCode]);
            if(Check.isNumber(count) && count > 0) {
                passengersCount[passengerTypeCode] = count;
            }
        }

        return passengersCount;
    }


    private _setLanguageAndCurrency(language: NullableUndefinedString, currency: NullableUndefinedString){
        if(!Check.isNullOrUndefined(language)) {
            this.services.language.changeCurrentLanguage(language);
        }

        if(!Check.isNullOrUndefined(currency)) {
            this.services.currency.setCurrentCurrency(currency);
        }
    }

    private _getDeepLinkSearchParams(): Record<string, string> {

        const queryParamsNames = [
            DeeplinkSearchQueryParamsEnum.Agent,
            DeeplinkSearchQueryParamsEnum.Culture,
            DeeplinkSearchQueryParamsEnum.Currency,
            DeeplinkSearchQueryParamsEnum.IsRoundTrip,
            DeeplinkSearchQueryParamsEnum.Origin1,
            DeeplinkSearchQueryParamsEnum.Destination1,
            DeeplinkSearchQueryParamsEnum.DepartureDate1,
            DeeplinkSearchQueryParamsEnum.ReturnDate1,
            DeeplinkSearchQueryParamsEnum.PsoUserOption,
            DeeplinkSearchQueryParamsEnum.PsoUserAgreement,
        ]

        return this.services.navigator.getQueryParamsValues(...queryParamsNames);
    }

    private _getPsoUserOptionQueryParam(queryParamsValues: Record<string, string>): PsoUserOptionsEnum {
        const value = queryParamsValues[DeeplinkSearchQueryParamsEnum.PsoUserOption];
        if(Check.isNullOrUndefined(value)) {
            return PsoUserOptionsEnum.None;
        }

        const parsedValue = parseInt(value);
        if(Check.isNumber(parsedValue)) {
            return parsedValue;
        }

        return PsoUserOptionsEnum.None;
    }

    private _getPsoUserAgreementQueryParam(queryParamsValues: Record<string, string>): PsoUserAgreementEnum {
        const value = queryParamsValues[DeeplinkSearchQueryParamsEnum.PsoUserAgreement];
        if(Check.isNullOrUndefined(value)) {
            return PsoUserAgreementEnum.None;
        }

        const parsedValue = parseInt(value);
        if(Check.isNumber(parsedValue)) {
            return parsedValue;
        }

        return PsoUserAgreementEnum.None;
    }


}
